import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSeguimientoStatus, exportWeeklyExcel, getUsers } from '../utils/api';
import TaskDrawer from '../components/TaskDrawer';

const pctColor = (p) => p >= 80 ? '#10B981' : p >= 50 ? '#F59E0B' : '#EF4444';

const STATUS_BADGE = {
  'En progreso':   { color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  'Bloqueada':     { color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
  'Pendiente':     { color: '#94A3B8', bg: 'rgba(148,163,184,0.08)' },
  'Recurrente':    { color: '#06B6D4', bg: 'rgba(6,182,212,0.08)' },
  'En espera de cliente': { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  'En espera de usuario/Presidencia': { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  'Lista para revisión': { color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
};

// ─── Fila de tarea dentro del acordeón ───────────────────────────────────────
const TaskRow = ({ item, onSelect }) => {
  const upd = item.update;
  const pct = upd?.cumplimiento_pct ?? item.task.cumplimiento_pct ?? null;
  const pc  = pct != null ? pctColor(pct) : '#94A3B8';
  const sBadge = STATUS_BADGE[item.task.status] || { color: '#94A3B8', bg: 'rgba(148,163,184,0.08)' };

  return (
    <div
      onClick={() => onSelect(item.task)}
      className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors rounded-xl"
      style={{
        background: !item.has_update ? 'rgba(245,158,11,0.04)' : 'transparent',
        border: `1px solid ${!item.has_update ? 'rgba(245,158,11,0.12)' : 'rgba(0,0,0,0.05)'}`,
      }}
    >
      {/* Indicador seguimiento */}
      <div className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5"
        style={{ background: item.has_update ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)' }}>
        <div className="w-1.5 h-1.5 rounded-full"
          style={{ background: item.has_update ? '#10B981' : '#F59E0B' }} />
      </div>

      {/* Contenido principal */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap mb-1">
          <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>{item.task.title}</p>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ color: sBadge.color, background: sBadge.bg }}>
            {item.task.status}
          </span>
        </div>
        {upd?.seguimiento || upd?.resultado ? (
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: '#475569' }}>
            {upd.seguimiento || upd.resultado}
          </p>
        ) : (
          <p className="text-xs italic" style={{ color: '#CBD5E1' }}>Sin seguimiento esta semana</p>
        )}
        {upd?.bloqueo && (
          <p className="text-xs mt-1 font-medium" style={{ color: '#EF4444' }}>🚫 {upd.bloqueo}</p>
        )}
        {upd?.apoyo_requerido && (
          <p className="text-xs mt-0.5 font-medium" style={{ color: '#F59E0B' }}>🙋 {upd.apoyo_requerido}</p>
        )}
      </div>

      {/* % cumplimiento */}
      {pct != null && (
        <div className="flex-shrink-0 text-right">
          <span className="text-sm font-black" style={{ color: pc }}>{pct}%</span>
          <div className="w-14 rounded-full overflow-hidden mt-1" style={{ height: 3, background: 'rgba(0,0,0,0.06)' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pc, borderRadius: 9999 }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Acordeón de una persona ──────────────────────────────────────────────────
const PersonAccordion = ({ person, onSelect, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const pp  = person.total ? Math.round((person.updated / person.total) * 100) : 0;
  const ppc = pctColor(pp);

  // Agrupar por responsabilidad
  const respGroups = {};
  person.items.forEach(item => {
    const key = item.task.responsabilidad || 'Sin responsabilidad';
    if (!respGroups[key]) respGroups[key] = [];
    respGroups[key].push(item);
  });
  const respKeys = Object.keys(respGroups).sort();

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', boxShadow: open ? '0 2px 12px rgba(0,0,0,0.05)' : 'none' }}>
      {/* Header persona */}
      <button className="w-full flex items-center gap-3 px-4 py-4 text-left transition-colors"
        style={{ background: open ? 'rgba(99,102,241,0.03)' : 'transparent' }}
        onClick={() => setOpen(o => !o)}>
        {/* Avatar */}
        <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm text-white flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#6366F1,#818CF8)' }}>
          {person.name.charAt(0).toUpperCase()}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm" style={{ color: '#0F172A' }}>{person.name}</p>
          {person.dept && (
            <p className="text-xs font-semibold" style={{ color: '#94A3B8' }}>{person.dept}</p>
          )}
        </div>
        {/* Progreso */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs font-black" style={{ color: ppc }}>{pp}%</p>
            <div className="w-16 rounded-full overflow-hidden mt-0.5" style={{ height: 3, background: 'rgba(0,0,0,0.06)' }}>
              <div style={{ width: `${pp}%`, height: '100%', background: ppc, borderRadius: 9999 }} />
            </div>
            <p className="text-[10px] mt-0.5" style={{ color: '#CBD5E1' }}>
              {person.updated}/{person.total}
            </p>
          </div>
          <svg className={`w-4 h-4 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
            style={{ color: '#CBD5E1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </button>

      {/* Tareas agrupadas por responsabilidad */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-3 pb-3 space-y-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              {respKeys.map(resp => (
                <div key={resp} className="pt-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest px-1 mb-2" style={{ color: '#94A3B8' }}>
                    {resp}
                  </p>
                  <div className="space-y-2">
                    {respGroups[resp].map(item => (
                      <TaskRow key={item.task.id} item={item} onSelect={onSelect} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Seguimiento Ejecutivo ────────────────────────────────────────────────────
const SeguimientoEjecutivo = () => {
  const [data, setData]           = useState(null);
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [filterDept, setFilterDept] = useState('');
  const [filterResp, setFilterResp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const getWeekStart = useCallback((offset = 0) => {
    const today = new Date();
    const dow = today.getDay();
    const daysSinceSat = (dow + 1) % 7;
    const sat = new Date(today);
    sat.setDate(today.getDate() - daysSinceSat - offset * 7);
    return sat.toISOString().split('T')[0];
  }, []);

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { loadData(); }, [weekOffset]);

  const loadUsers = async () => {
    try { const res = await getUsers(); setUsers(res.data || []); } catch {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const ws = getWeekStart(weekOffset);
      const res = await getSeguimientoStatus(ws, true);
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  const getDeptForUser = (userId) => {
    const u = users.find(u => u.id === userId);
    return u?.department_name && u.department_name !== 'Sin Departamento' ? u.department_name : '';
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const ws = getWeekStart(weekOffset);
      const res = await exportWeeklyExcel(ws);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexia_seguimiento_${ws}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
    setExporting(false);
  };

  const items = data?.items || [];
  const completed = data?.completed_items || [];

  // Filtrar
  const filteredItems = items.filter(i => {
    const dept = getDeptForUser(i.task.responsible_user_id);
    if (filterDept && dept !== filterDept) return false;
    if (filterResp && i.responsible_name !== filterResp) return false;
    if (filterStatus === 'sin_update' && i.has_update) return false;
    if (filterStatus === 'con_update' && !i.has_update) return false;
    return true;
  });

  // Agrupar por persona
  const personMap = {};
  filteredItems.forEach(item => {
    const uid = item.task.responsible_user_id || '__';
    if (!personMap[uid]) {
      personMap[uid] = {
        id: uid,
        name: item.responsible_name,
        dept: getDeptForUser(uid),
        items: [],
        total: 0,
        updated: 0,
      };
    }
    personMap[uid].items.push(item);
    personMap[uid].total++;
    if (item.has_update) personMap[uid].updated++;
  });

  // Ordenar personas: más bajo cumplimiento primero
  const persons = Object.values(personMap).sort((a, b) => {
    const aR = a.total ? a.updated / a.total : 1;
    const bR = b.total ? b.updated / b.total : 1;
    return aR - bR;
  });

  const depts = [...new Set(items.map(i => getDeptForUser(i.task.responsible_user_id)))].filter(Boolean).sort();
  const resps = [...new Set(items.map(i => i.responsible_name))].filter(Boolean).sort();
  const totalUpdated = items.filter(i => i.has_update).length;
  const compliance   = items.length > 0 ? Math.round((totalUpdated / items.length) * 100) : 0;
  const pc           = pctColor(compliance);

  const weekLabel = data?.week_start
    ? new Date(data.week_start).toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="min-h-screen" style={{ background: '#F1F5F9' }}>

      {/* Header sticky */}
      <div className="sticky top-14 md:top-0 z-10 px-4 md:px-6 py-4"
        style={{ background: 'rgba(10,26,20,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1.5px solid rgba(0,208,132,0.25)' }}>
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-black"
              style={{ background: 'linear-gradient(135deg,#00D084,#00FFB3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Seguimiento Ejecutivo
            </h1>
            <p className="text-xs font-semibold mt-0.5" style={{ color: '#A0F0D0' }}>{weekLabel}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setWeekOffset(w => w + 1)} className="btn-secondary px-3 py-1.5 text-xs">← Anterior</button>
            <button onClick={() => setWeekOffset(0)} disabled={weekOffset === 0} className="btn-secondary px-3 py-1.5 text-xs disabled:opacity-40">Esta semana</button>
            <button onClick={() => setWeekOffset(w => Math.max(0, w - 1))} disabled={weekOffset === 0} className="btn-secondary px-3 py-1.5 text-xs disabled:opacity-40">Siguiente →</button>
            <button onClick={handleExport} disabled={exporting} className="btn-primary px-4 py-1.5 text-xs">
              {exporting ? '...' : '📥 CSV'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total tareas',   value: items.length,                color: '#0F172A' },
            { label: 'Actualizadas',   value: totalUpdated,                color: '#10B981' },
            { label: 'Sin actualizar', value: items.length - totalUpdated, color: '#EF4444' },
            { label: 'Cumplimiento',   value: `${compliance}%`,            color: pc        },
          ].map(kpi => (
            <div key={kpi.label} className="rounded-2xl p-4 text-center"
              style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
              <div className="text-2xl font-black" style={{ color: kpi.color }}>{kpi.value}</div>
              <div className="text-xs mt-1 font-semibold" style={{ color: '#94A3B8' }}>{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Barra global */}
        <div className="rounded-2xl px-4 py-3" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#94A3B8' }}>Cumplimiento global</span>
            <span className="text-sm font-black" style={{ color: pc }}>{compliance}%</span>
          </div>
          <div className="rounded-full overflow-hidden" style={{ height: 5, background: 'rgba(0,0,0,0.06)' }}>
            <motion.div initial={{ width: 0 }} animate={{ width: `${compliance}%` }} transition={{ duration: 0.8 }}
              style={{ height: '100%', background: pc, borderRadius: 9999 }} />
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2">
          <select className="input-decko text-sm" value={filterDept} onChange={e => setFilterDept(e.target.value)}>
            <option value="">Todos los departamentos</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="input-decko text-sm" value={filterResp} onChange={e => setFilterResp(e.target.value)}>
            <option value="">Todos los responsables</option>
            {resps.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="input-decko text-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="sin_update">⚠️ Sin actualizar</option>
            <option value="con_update">✅ Actualizadas</option>
          </select>
          {(filterDept || filterResp || filterStatus) && (
            <button onClick={() => { setFilterDept(''); setFilterResp(''); setFilterStatus(''); }}
              className="btn-secondary text-xs">Limpiar</button>
          )}
        </div>

        {/* Acordeón por persona */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 rounded-full animate-spin"
              style={{ borderColor: '#00C878', borderTopColor: 'transparent' }} />
          </div>
        ) : persons.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <p className="text-4xl mb-3">📋</p>
            <p className="font-semibold" style={{ color: '#1E293B' }}>Sin tareas en este filtro</p>
          </div>
        ) : (
          <div className="space-y-3">
            {persons.map((person, idx) => (
              <PersonAccordion
                key={person.id}
                person={person}
                onSelect={setSelectedTask}
                defaultOpen={idx === 0}
              />
            ))}
          </div>
        )}

        {/* Cerradas / No realizadas (colapsable, al final) */}
        {!loading && completed.length > 0 && (
          <ClosedAccordion items={completed} onSelect={setSelectedTask} getDept={getDeptForUser} />
        )}

        <div style={{ height: 40 }} />
      </div>

      {selectedTask && (
        <TaskDrawer
          task={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

// ─── Sección colapsable para cerradas ────────────────────────────────────────
const ClosedAccordion = ({ items, onSelect, getDept }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#94A3B8' }}>Historial</p>
          <p className="text-sm font-bold" style={{ color: '#64748B' }}>
            Cerradas / No realizadas
            <span className="ml-2 font-normal text-xs" style={{ color: '#CBD5E1' }}>({items.length})</span>
          </p>
        </div>
        <svg className={`w-4 h-4 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          style={{ color: '#CBD5E1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-3 pb-3 space-y-2 opacity-60" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="pt-2 space-y-1.5">
                {items.map(item => (
                  <div key={item.task.id}
                    onClick={() => onSelect(item.task)}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer"
                    style={{ background: 'rgba(0,0,0,0.02)' }}>
                    <span className="text-sm flex-shrink-0">
                      {item.task.status === 'Aprobada / Cerrada' ? '✅' : '❌'}
                    </span>
                    <div className="flex-1 min-w-0">
                      {item.task.responsabilidad && (
                        <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#CBD5E1' }}>
                          {item.task.responsabilidad}
                        </p>
                      )}
                      <p className="text-xs font-medium truncate"
                        style={{ color: '#64748B', textDecoration: item.task.status === 'Aprobada / Cerrada' ? 'line-through' : 'none' }}>
                        {item.task.title}
                      </p>
                    </div>
                    <span className="text-xs flex-shrink-0" style={{ color: '#94A3B8' }}>{item.responsible_name}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SeguimientoEjecutivo;
